import { createApp } from 'vue'
import ConfirmDialog from './ConfirmDialog.vue'

interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'warning' | 'danger' | 'info'
  showCancel?: boolean
  dismissOnOverlay?: boolean
}

interface PromptOptions extends ConfirmOptions {
  inputLabel?: string
  inputValue?: string
  inputPlaceholder?: string
  inputMaxlength?: number
}

let dialogInstance: InstanceType<typeof ConfirmDialog> | null = null

function getDialogInstance(): InstanceType<typeof ConfirmDialog> {
  if (!dialogInstance) {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const app = createApp(ConfirmDialog)
    dialogInstance = app.mount(container) as InstanceType<typeof ConfirmDialog>
  }
  return dialogInstance
}

/**
 * 对话框是全局单例，同时只能展示一个。这里用两条串行队列排队：
 *
 * - decision 队列：confirm / prompt。抢占会把挂起者的 Promise 结算为 false，
 *   而 false 在部分调用方等于"用户点了取消"——可能触发退出登录、丢弃草稿等副作用，
 *   因此它们必须排在上一个决策对话框之后。
 * - info 队列：alert。只在对话框完全空闲时展示，绝不抢占决策对话框。
 */
let decisionChain: Promise<unknown> = Promise.resolve()
let infoChain: Promise<unknown> = Promise.resolve()

async function confirm(message: string, title = '提示', options: Partial<ConfirmOptions> = {}): Promise<boolean> {
  const run = async (): Promise<boolean> => {
    const instance = getDialogInstance()
    // 等任何对话框（包括 alert）关闭，而不是只等决策对话框：否则会抢占正在展示的
    // alert，把它的内容在用户读到之前替换掉。所有弹窗按入队顺序先进先出。
    if (instance.isShowing()) {
      await instance.waitUntilClosed()
    }

    return instance.show({
      title,
      message,
      confirmText: options.confirmText || '确定',
      cancelText: options.cancelText || '取消',
      type: options.type || 'warning',
      dismissOnOverlay: options.dismissOnOverlay !== false
    })
  }

  const next = decisionChain.then(run, run)
  decisionChain = next.catch(() => false)
  return next
}

async function confirmDelete(itemName: string): Promise<boolean> {
  const run = async (): Promise<boolean> => {
    const instance = getDialogInstance()
    if (instance.isShowing()) {
      await instance.waitUntilClosed()
    }

    return instance.show({
      title: '确认删除',
      message: `确定要删除"${itemName}"吗？\n此操作不可恢复，请谨慎操作。`,
      confirmText: '删除',
      cancelText: '取消',
      type: 'danger'
    })
  }

  const next = decisionChain.then(run, run)
  decisionChain = next.catch(() => false)
  return next
}

async function alert(
  message: string,
  title = '提示',
  options: Partial<ConfirmOptions> = {}
): Promise<void> {
  const run = async (): Promise<void> => {
    const instance = getDialogInstance()

    // 绝不抢占正在展示的对话框：它可能是需要用户决策的 confirm，
    // 抢占会让它被结算为 false（等同"用户取消"），造成静默的副作用。
    if (instance.isShowing()) {
      await instance.waitUntilClosed()
    }

    await instance.show({
      title,
      message,
      confirmText: options.confirmText || '我知道了',
      cancelText: options.cancelText || '取消',
      type: options.type || 'warning',
      showCancel: false,
      dismissOnOverlay: options.dismissOnOverlay !== false
    })
  }

  const next = infoChain.then(run, run)
  infoChain = next.catch(() => undefined)
  return next
}

async function prompt(
  message: string,
  title = '请输入',
  options: Partial<PromptOptions> = {}
): Promise<string | null> {
  const run = async (): Promise<string | null> => {
    const instance = getDialogInstance()
    if (instance.isShowing()) {
      await instance.waitUntilClosed()
    }

    return instance.prompt({
      title,
      message,
      confirmText: options.confirmText || '确定',
      cancelText: options.cancelText || '取消',
      type: options.type || 'warning',
      dismissOnOverlay: options.dismissOnOverlay !== false,
      inputLabel: options.inputLabel,
      inputValue: options.inputValue,
      inputPlaceholder: options.inputPlaceholder,
      inputMaxlength: options.inputMaxlength
    })
  }

  const next = decisionChain.then(run, run)
  decisionChain = next.catch(() => null)
  return next
}

const MessageBox = {
  confirm,
  confirmDelete,
  alert,
  prompt
}

export default MessageBox
